import { css, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { EventId } from "../EventSystem";

import { Kernel } from "../Kernel";
import { Style } from "./Styles";
import { makeCustomEvent } from "../Utils";

@customElement("ds-base-element")
export class BaseElement extends LitElement {
    static override styles = [Style, css``];

    protected _subscribedEvents: { event: string; id: EventId }[] = [];
    protected _eventListeners: { event: string; listener: EventListenerOrEventListenerObject }[] =
        [];
    protected _kernel = Kernel.instance();
    @state() protected _currentDialog?: string;

    public override async connectedCallback(): Promise<void> {
        super.connectedCallback();
        this._subscribeToKernelEvent("user-settings-changed", () => this.requestUpdate());
    }

    public override disconnectedCallback(): void {
        super.disconnectedCallback();
        this._subscribedEvents.forEach((x) => this._kernel.events.unsubscribe(x.event, x.id));
        this._subscribedEvents = [];

        this._eventListeners.forEach((x) => this.removeEventListener(x.event, x.listener));
        this._eventListeners = [];
    }

    protected _openDialog(name: string) {
        this._closeDrawer();
        this._currentDialog = name;
    }

    protected _closeDialog() {
        this._currentDialog = undefined;
    }

    protected _closeDrawer() {}

    protected _dialogAfterHide(e: Event) {
        if (e.eventPhase === e.AT_TARGET) {
            this._currentDialog = undefined;
            this._onDialogAfterHide();
            return true;
        }
        return false;
    }

    protected _onDialogAfterHide() {}

    /**
     * Subscribes to kernel event and will automatically remove it on disconnect.
     * These must be added in the connectedCallback() function in this or a derived class.
     */
    protected _subscribeToKernelEvent(eventNameOrArray: string | string[], callback: any) {
        let names: string[];
        if (!Array.isArray(eventNameOrArray)) {
            names = [eventNameOrArray];
        } else {
            names = eventNameOrArray;
        }
        names.forEach((eventName) =>
            this._subscribedEvents.push({
                event: eventName,
                id: this._kernel.events.subscribe(eventName, callback),
            })
        );
    }

    public override addEventListener(
        type: string,
        listener: EventListenerOrEventListenerObject | ((e: CustomEvent) => void),
        options?: boolean | AddEventListenerOptions | undefined
    ): void {
        super.addEventListener(type, listener as EventListenerOrEventListenerObject, options);
        this._eventListeners.push({
            event: type,
            listener: listener as EventListenerOrEventListenerObject,
        });
    }

    protected _raiseEvent(name: string, details: object = {}) {
        this.dispatchEvent(makeCustomEvent(name, details));
    }
}
